import React, { useEffect, useState, useRef, RefObject } from 'react';
import './Select.scss';

export type SelectProps = {
  id: string
  label: string;
  value: string;
  error?: string | boolean;
  span?: string | undefined;
  onChange(value: string): void;
  onBlur?(): void;
  options: string[];
  optionLabels?: {[key: string]: string};
};


const scrollToItem = (listRef: RefObject<HTMLDivElement>, index: number) => {
  if (listRef.current) {
    const item = listRef.current.children[index];
    if (item) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }
};

export const Select: React.FunctionComponent<SelectProps> = (props: SelectProps): JSX.Element => {
  const {
    onChange,
    options,
    optionLabels,
    onBlur,
    error,
    value,
    label,
    span,
    id,
  } = props;

  const selectClassName = `
    text-sm outline w-full leading-tight
    input-outline bg-white
    ${error ? ' border-red-600' : ''}
  `;

  const labelClassName = 'block font-bold mt-3 text-sm mb-2';

  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string>(value);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const selectRef: RefObject<HTMLDivElement> | null = useRef<HTMLDivElement>(null);
  const optionsRef: RefObject<HTMLDivElement> | null = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<number>(0);
  const searchedValueRef = useRef<string>('');
  const isOptionSelected = Boolean(selectedOption);

  const onKeyUpEventHandler = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    if (!showDropDown) {
      if (e.key === 'Enter' || e.key === '' || e.key === 'ArrowDown') {
        e.preventDefault();
        setShowDropDown(true);
        setHighlightedIndex(-1);
      }
      return;
    }

    switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev < options.length - 1 ? prev + 1 : 0
      );
      break;
    case 'ArrowUp':
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev > 0 ? prev - 1 : options.length - 1
      );
      break;
    case 'Escape':
      e.preventDefault();
      setShowDropDown(false);
      setHighlightedIndex(-1);
      break;
    case 'Enter':
      e.preventDefault();
      if (highlightedIndex >= 0) {
        selectItem(options[highlightedIndex]);
        setShowDropDown(false);
        setHighlightedIndex(-1);
      }
      break;
    case 'Tab':
      setShowDropDown(false);
      setHighlightedIndex(-1);
      onBlur?.();
      break;
    default:
      if (e.key.length === 1 || e.key === '') {
        e.preventDefault();
        window.clearTimeout(searchTimeoutRef.current);
        const currentValue = searchedValueRef.current;
        searchedValueRef.current = currentValue + e.key.toLowerCase();

        const matchIndex = options.findIndex(option =>
          option.toLowerCase().startsWith(searchedValueRef.current)
        );
        if (matchIndex !== -1) {
          setHighlightedIndex(matchIndex);
          scrollToItem(optionsRef, matchIndex);
        }
        searchTimeoutRef.current = window.setTimeout(() => {
          searchedValueRef.current = '';
        }, 1000);
      }
    }
  };

  const toggleDropDown = () => {
    setShowDropDown(prev => !prev);
    if (!showDropDown) {
      setHighlightedIndex(-1);
    }
  };

  const selectItem = (item: string) => {
    onChange(item);
    setSelectedOption(optionLabels?.[item] || item);
    setShowDropDown(false);
  };

  const handleOutsideClick = (e: Event) => {
    const target = e.target as HTMLElement;
    if (selectRef?.current?.contains(target)) {
      return;
    }
    setShowDropDown(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && showDropDown) {
      scrollToItem(optionsRef, highlightedIndex);
    }
  }, [highlightedIndex, showDropDown]);

  return (
    <>
      <div className='flex'>
        <label className={labelClassName} title={label} htmlFor={id}>
          <span className='mr-1'>{label}</span>
          <span className='mt-3 text-sm mb-2 font-normal'>{span}</span>
        </label>
      </div>
      <div
        ref={selectRef}
        className={`custom-select ${selectClassName}`}
      >
        <div>
          <div
            className='custom-select--option'
            onClick={toggleDropDown}
          >
            <button
              className='custom-select__selected-item input-outline text-ellipsis px-3 py-2 text-left'
              onBlur={onBlur}
              onKeyDown={onKeyUpEventHandler}
              tabIndex={0}
            >
              {optionLabels?.[value] || value || 'Select an option'}
            </button>
            <div className={`custom-select__arrow ${showDropDown ? 'custom-select__arrow--open' : ''}`} />
          </div>
          {showDropDown &&
            <div
              className={`custom-select__options outline`}
              ref={optionsRef}
            >
              {options.map((option, i) => {
                const isSelected = (isOptionSelected && value === option)
                  || (isOptionSelected && selectedOption === (optionLabels?.[option] || option))
                  || (highlightedIndex === i)
                ;
                return (
                  <button
                    key={i}
                    onClick={() => selectItem(option)}
                    className={`option px-3 ${isSelected ? 'selected' : ''}`}
                    role='option'
                    aria-selected={isSelected}
                  >
                    {optionLabels?.[option] || option || 'Select an option'}
                  </button>
                );})}
            </div>
          }
        </div>
      </div>
      {error && (typeof error === 'boolean') && <span className='text-red-600 text-xs py-4'>{error}</span>}
    </>
  );
};

export default Select;
